import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Button,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { findUser } from '../../../../store/users/api';
import { Users } from '../../../../store/users/api/interfaces';
import Iconify from '../../../../components/Iconify';
import debounce from 'lodash/debounce';
import CreateCustomerDialog from './CreateCustomerDialog';

interface CustomerSelectorProps {
  onCustomerSelect: (customer: Users | null) => void;
  selectedCustomer: Users | null;
}

const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  onCustomerSelect,
  selectedCustomer,
}) => {
  const dispatch = useAppDispatch();
  const { data: customers, isLoading } = useAppSelector((state) => state.user);
  const [searchText, setSearchText] = useState<string>('');
  const [options, setOptions] = useState<Users[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    // Fetch customers when component mounts
    dispatch(
      findUser({
        limit: 100,
        skip: 0,
        isUser: true, // Only get customers
      })
    );
  }, [dispatch]);

  useEffect(() => {
    // Update options when customers data changes
    // Handle both array and undefined/null cases
    if (Array.isArray(customers)) {
      setOptions(customers);
    } else if (customers === null || customers === undefined) {
      setOptions([]);
    }
  }, [customers]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      if (searchValue.length >= 1) {
        // Search after 1 character (changed from 2)
        dispatch(
          findUser({
            limit: 100,
            skip: 0,
            isUser: true,
            search: searchValue,
          })
        );
      } else {
        // Reset to all customers when search is empty
        dispatch(
          findUser({
            limit: 100,
            skip: 0,
            isUser: true,
          })
        );
      }
    }, 300),
    [dispatch]
  );

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleCreateCustomerSuccess = (newCustomer: Users) => {
    // Refresh customer list
    dispatch(
      findUser({
        limit: 100,
        skip: 0,
        isUser: true,
      })
    );
    // Auto-select the newly created customer
    onCustomerSelect(newCustomer);
    setCreateDialogOpen(false);
  };

  return (
    <>
    <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
      <Typography variant='h6' gutterBottom>
        Select Customer
      </Typography>
            <Typography variant='body2' color='text.secondary'>
        Search and select a customer to create an order for
      </Typography>
          </Box>
          <Button
            variant='contained'
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<Iconify icon='eva:plus-fill' />}
            sx={{
              backgroundColor: '#EE3750 !important',
              color: '#fff !important',
              '&:hover': {
                backgroundColor: '#EE3750 !important',
              },
            }}
          >
            Create Customer
          </Button>
        </Box>

      <Autocomplete
        value={selectedCustomer}
        onChange={(event, newValue) => {
          onCustomerSelect(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          handleSearchChange(newInputValue);
        }}
        options={options}
        getOptionLabel={(option) =>
          `${option.firstName} ${option.lastName || ''} - ${option.email || option.mobileNumber}`.trim()
        }
        loading={isLoading}
        filterOptions={(options) => options} // Disable client-side filtering since we're doing server-side search
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Search by name, email, or phone number'
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <Iconify
                    icon={'eva:search-fill'}
                    sx={{ color: 'text.disabled', width: 20, height: 20 }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress
                      color='inherit'
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component='li'
            {...props}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}
          >
            <Typography variant='body1'>
              {option.firstName} {option.lastName}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {option.email || 'No email'} • {option.countryCallingCode}-{option.mobileNumber}
            </Typography>
            {option.balance && (
              <Typography variant='caption' color='text.secondary'>
                Balance: {option.balance}
              </Typography>
            )}
          </Box>
        )}
        noOptionsText={searchText ? 'No customers found' : 'Start typing to search...'}
        sx={{ width: '100%' }}
      />

      {selectedCustomer && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#FDF0F1', borderRadius: 1 }}>
          <Typography variant='subtitle2' gutterBottom>
            Selected Customer:
          </Typography>
          <Typography variant='body2'>
            <strong>Name:</strong> {selectedCustomer.firstName} {selectedCustomer.lastName}
          </Typography>
          <Typography variant='body2'>
            <strong>Email:</strong> {selectedCustomer.email || 'N/A'}
          </Typography>
          <Typography variant='body2'>
            <strong>Phone:</strong> {selectedCustomer.countryCallingCode}-{selectedCustomer.mobileNumber}
          </Typography>
          {selectedCustomer.balance && (
            <Typography variant='body2'>
              <strong>Wallet Balance:</strong> {selectedCustomer.balance}
            </Typography>
          )}
        </Box>
      )}
    </Card>

      <CreateCustomerDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateCustomerSuccess}
      />
    </>
  );
};

export default CustomerSelector;

